/**
 * /financeiro/rentabilidade — Rentabilidade por pedido (ADR-0030) · redesign FIN-6.
 *
 * Fiel ao mockup Fase 5 V2.2 (#rent): scatter "Margem × Receita" (cada ponto =
 * 1 pedido, tamanho = lucro) + ranking "Top 10 por margem" com margin-pill.
 *
 * Camada visual; consome /api/financeiro/rentabilidade que já existe. Nenhum
 * cálculo financeiro novo — o backend entrega receita/tvc/oe/lucro/margem.
 *
 * Nota de fidelidade: o mockup plota "Margem × Lead Time", mas o endpoint não
 * expõe lead time por pedido. Para não inventar dado, o eixo X usa a receita
 * (dimensão real do payload). Quadrantes ideal/problema seguem pela margem (Y).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FiAlertTriangle } from "react-icons/fi";
import { Link } from "react-router-dom";

import ExportMenu from "../../../components/ExportMenu";
import { useAuth } from "../../../context/AuthContext";
import { formatarReais, obterRentabilidadePeriodo } from "../../../services/financeiro";
import { PageContainer } from "../../../styles/shared";
import { exportarRentabilidade } from "../_shared/exportarRelatorios";

function isoLocal(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function fmtK(v) {
  const n = Number(v || 0);
  const a = Math.abs(n);
  if (a >= 1000) return `R$ ${(n / 1000).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}k`;
  return formatarReais(n);
}
function pct1(v) { return `${Number(v || 0).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%`; }

// Faixa de margem → semântica (alinha com .margin-pill high/mid/low).
function banda(margem) {
  const m = Number(margem || 0);
  if (m >= 30) return "high";
  if (m >= 20) return "mid";
  return "low";
}

const PERIODOS = [
  { id: "30d", label: "30d" },
  { id: "90d", label: "90d" },
  { id: "ytd", label: "YTD" },
];

function intervaloDe(p, base = new Date()) {
  if (p === "ytd") return [`${base.getFullYear()}-01-01`, isoLocal(base)];
  const dias = p === "30d" ? 30 : 90;
  const ini = new Date(base); ini.setDate(ini.getDate() - dias);
  return [isoLocal(ini), isoLocal(base)];
}

const COR_BANDA = {
  high: "var(--success-solid)",
  mid: "var(--warning-solid)",
  low: "var(--danger-solid)",
};


/** Scatter SVG honesto: X = receita, Y = margem %, raio = lucro. */
function ScatterMargem({ pedidos }) {
  const W = 540, H = 360;
  const x0 = 40, x1 = 510, y0 = 30, y1 = 330;
  const yMax = 50; // % teto do eixo
  const maxReceita = Math.max(1, ...pedidos.map((p) => Number(p.receita || 0)));
  const maxLucro = Math.max(1, ...pedidos.map((p) => Number(p.lucro_liquido || 0)));

  const X = (r) => x0 + Math.min(1, Number(r || 0) / maxReceita) * (x1 - x0);
  const Y = (m) => y1 - Math.min(1, Math.max(0, Number(m || 0) / yMax)) * (y1 - y0);
  const R = (l) => 5 + Math.min(1, Number(l || 0) / maxLucro) * 11;

  const yTicks = [0, 10, 20, 30, 40, 50];
  const xTicks = [0, 0.25, 0.5, 0.75, 1];

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: 360 }} role="img"
         aria-label="Dispersão de margem por receita">
      {yTicks.map((t) => (
        <line key={`g${t}`} className="chart-grid-line" x1={x0} y1={Y(t)} x2={x1} y2={Y(t)} />
      ))}
      {/* quadrantes: linha de margem-alvo 20% + divisor de receita na metade */}
      <line x1={x0} y1={Y(20)} x2={x1} y2={Y(20)} stroke="var(--border-strong)" strokeDasharray="3 3" />
      <line x1={(x0 + x1) / 2} y1={y0} x2={(x0 + x1) / 2} y2={y1} stroke="var(--border-strong)" strokeDasharray="3 3" />
      <text x={x0 + 8} y={y0 + 14} fontSize="10" fill="var(--success-text)" fontWeight="600"
            style={{ fontFamily: "var(--font-mono)" }}>★ IDEAL</text>
      <text x={x0 + 8} y={y1 - 6} fontSize="10" fill="var(--danger-text)" fontWeight="600"
            style={{ fontFamily: "var(--font-mono)" }}>⚠ PROBLEMA</text>
      {yTicks.map((t) => (
        <text key={`y${t}`} className="chart-y-text" x={x0 - 6} y={Y(t) + 3} textAnchor="end">{t}%</text>
      ))}
      {xTicks.map((f) => (
        <text key={`x${f}`} className="chart-axis-text" x={X(maxReceita * f)} y={y1 + 16} textAnchor="middle">
          {fmtK(maxReceita * f)}
        </text>
      ))}
      {pedidos.map((p) => (
        <circle key={p.pedido_id || p.numero} cx={X(p.receita)} cy={Y(p.margem_liquida)} r={R(p.lucro_liquido)}
                fill={COR_BANDA[banda(p.margem_liquida)]} fillOpacity="0.78">
          <title>{`${p.numero} · ${pct1(p.margem_liquida)} · ${formatarReais(p.receita)}`}</title>
        </circle>
      ))}
    </svg>
  );
}


export default function Rentabilidade() {
  const { request } = useAuth();
  const hoje = useMemo(() => new Date(), []);
  const [periodo, setPeriodo] = useState("90d");
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [inicio, fim] = useMemo(() => intervaloDe(periodo, hoje), [periodo, hoje]);

  const carregar = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const d = await obterRentabilidadePeriodo(request, { inicio, fim, situacao: "ENTREGUE" });
      setDados(d);
    } catch (err) {
      setError(err?.message || "Erro ao carregar rentabilidade.");
    } finally { setLoading(false); }
  }, [request, inicio, fim]);

  useEffect(() => { carregar(); }, [carregar]);

  const pedidos = dados?.pedidos || [];
  const top10 = pedidos.slice(0, 10);
  const t = dados?.totais || {};
  const pedidosRevisados = pedidos.filter((p) => p.tvc_desatualizado);
  const temDados = pedidos.length > 0;
  const periodoLabel = PERIODOS.find((p) => p.id === periodo)?.label || "";

  return (
    <PageContainer style={{ maxWidth: "none" }} className="fin2">
      <div className="flex between" style={{ alignItems: "flex-end", marginBottom: 18, flexWrap: "wrap", gap: 12 }}>
        <div>
          <div className="breadcrumb">
            <span>Início</span><span className="crumb-sep">/</span>
            <span>Financeiro</span><span className="crumb-sep">/</span>
            <span className="active">Rentabilidade</span>
          </div>
          <h1 className="page-title">Rentabilidade por Pedido</h1>
          <div className="page-sub">Receita − TVC − OE rateado = lucro real · pedidos entregues</div>
        </div>
        <div className="flex" style={{ gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <div className="tabchip-group">
            {PERIODOS.map((p) => (
              <div key={p.id} className={"tabchip" + (periodo === p.id ? " active" : "")}
                   role="button" tabIndex={0} onClick={() => setPeriodo(p.id)}
                   onKeyDown={(e) => e.key === "Enter" && setPeriodo(p.id)}>{p.label}</div>
            ))}
          </div>
          <ExportMenu disabled={!temDados}
                      onExport={(formato) => dados && exportarRentabilidade(formato, dados)} />
        </div>
      </div>

      <div className="kpi-strip-v2 standalone" style={{ marginBottom: 14 }}>
        <div className="kpi-v2"><div className="lbl">Receita</div><div className="val">{formatarReais(t.receita)}</div><div className="meta">{dados?.pedidos_count || 0} pedidos</div></div>
        {/* ADR-0061 D4: dois custos, cada um com sua régua. O orçado é o
            que o vendedor prometeu; o realizado é o que a fábrica gastou.
            A diferença entre eles é o que se está perdendo. */}
        <div className="kpi-v2"><div className="lbl">Custo orçado (TVC)</div><div className="val">{formatarReais(t.tvc)}</div><div className="meta">congelado na proposta</div></div>
        <div className="kpi-v2"><div className="lbl">OE rateado</div><div className="val">{formatarReais(t.oe_rateado)}</div><div className="meta">despesa operacional</div></div>
        {/* M-FIN-3: o dashboard mostra "Resultado (T − OE)" do mes inteiro e
            esta tela mostra lucro POR PEDIDO com o custo fixo rateado. Sao
            duas contas legitimas e diferentes; sem dizer qual e qual, o
            gestor ve dois "lucros" do mesmo mes e nao sabe em qual acreditar. */}
        <div className="kpi-v2"><div className="lbl">Lucro dos pedidos entregues</div><div className="val" style={{ color: Number(t.lucro_liquido || 0) >= 0 ? "var(--success-text)" : "var(--danger-text)" }}>{formatarReais(t.lucro_liquido)}</div><div className="meta" title="Receita − custo direto − custo fixo rateado por pedido. Não fecha com o Resultado (T − OE) do dashboard, que é do mês inteiro em regime de competência.">margem {pct1(t.margem_media)} · não é o Resultado do mês</div></div>
      </div>

      {/* D-6E: o OE conta so DESPESA_*; o que esta em "Outros" fica de fora.
          A exclusao tem que ser acao do gestor, nao silencio — por isso o
          valor omitido aparece aqui com o caminho para classificar. */}
      {Number(t.oe_nao_classificado || 0) > 0 && (
        <div className="empty" style={{ marginBottom: 12 }}>
          <div className="msg">
            <FiAlertTriangle size={14} aria-hidden="true" />{" "}
            {formatarReais(t.oe_nao_classificado)} em saídas ficaram FORA do custo fixo
            por estarem na classificação “Outros”. Classifique as categorias em{" "}
            <Link to="/financeiro/cadastros">Cadastros → Categorias</Link> para o lucro
            fechar.
          </div>
        </div>
      )}

      {/* A16: pedido revisado depois do orcamento tem receita nova e custo
          velho — a margem sai deslocada e o usuario precisa saber. */}
      {pedidosRevisados.length > 0 && (
        <div className="empty" style={{ marginBottom: 12 }}>
          <div className="msg">
            <FiAlertTriangle size={14} aria-hidden="true" />{" "}
            {pedidosRevisados.length === 1
              ? `O pedido ${pedidosRevisados[0].numero} foi revisado depois do orçamento`
              : `${pedidosRevisados.length} pedidos foram revisados depois do orçamento`}
            : a receita acompanhou a revisão, o custo direto não. A margem deles é
            estimativa.
          </div>
        </div>
      )}

      {error && <div className="empty"><div className="msg"><FiAlertTriangle size={14} aria-hidden="true" /> {error}</div></div>}

      <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 14, alignItems: "start" }}>
        <div className="cc">
          <div className="cc__head">
            <div>
              <div className="cc__title">Margem × Receita</div>
              <div className="cc__sub">cada ponto = 1 pedido · tamanho = lucro · {periodoLabel}</div>
            </div>
          </div>
          <div className="cc__body">
            {temDados ? <ScatterMargem pedidos={pedidos} /> : (
              <div className="empty"><div className="msg">{loading ? "Carregando…" : "Sem pedidos entregues no período."}</div></div>
            )}
          </div>
        </div>

        <div className="block">
          <div className="block__head">
            <div className="block__title">Top 10 por margem</div>
            <span className="tiny mono muted">{periodoLabel}</span>
          </div>
          <div className="block__body tight">
            {!temDados && (
              <div className="empty"><div className="msg">{loading ? "Carregando…" : "Sem dados."}</div></div>
            )}
            {top10.map((p, i) => (
              <div key={p.pedido_id || p.numero} className="prof-row">
                <span className={"pos" + (i < 3 ? " top" : "")}>{i + 1}</span>
                <div className="nm">
                  <strong>{p.numero}</strong>
                  {p.tvc_desatualizado ? (
                    <span className="tag" title="Pedido revisado depois do orçamento — o custo direto é o do orçamento original.">
                      {" "}margem estimada
                    </span>
                  ) : null}
                  <div className="sub ell" title={p.cliente}>{p.cliente}</div>
                  {/* ADR-0061 D4: o lucro que a fábrica entregou, ao lado
                      do que o vendedor prometeu. `null` quando a produção
                      ainda não baixou material — "—" é honesto, um número
                      ali seria inventado. */}
                  {p.lucro_realizado != null ? (
                    <div className="sub" title="Receita − custo REAL do material que saiu do estoque − OE rateado.">
                      realizado {fmtK(p.lucro_realizado)}
                      {p.margem_realizada != null ? ` · ${pct1(p.margem_realizada)}` : ""}
                    </div>
                  ) : null}
                </div>
                <span className="num">{fmtK(p.receita)}</span>
                <span className={"margin-pill " + banda(p.margem_liquida)}>{pct1(p.margem_liquida)}</span>
                <span className="num" style={{ color: "var(--success-text)", fontWeight: 600 }}>{fmtK(p.lucro_liquido)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </PageContainer>
  );
}
